"use client";

import * as React from "react";
import { useTranslation } from "../contexts/I18nProvider";
import {
  NavigationMenu,
  NavigationMenuItem,
  NavigationMenuList,
  navigationMenuTriggerStyle,
} from "../components/ui/navigation-menu";
import { Link, useLocation } from "react-router-dom";
import { Logo } from "../layouts/home";

import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "./ui/drawer";

import { Button } from "./ui/button";
import { Menu } from "lucide-react";
import { API_BASE_URL } from "../lib/constants";
import { useLoggedIn } from "../hooks/use-logged-in";
import Cookie, { JWT_COOKIE_NAME } from "../lib/cookie";
import { LanguageSelector } from "./LanguageSelector";
import { ChevronDown, LogOut, User } from "lucide-react";
import { ThemeToggle } from "./ThemeToggle";

// Account Dropdown Component
function AccountDropdown() {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = React.useState(false);

  return (
    <div className="relative group">
      <Button 
        variant="outline" 
        className="flex items-center gap-2 rounded-xl"
        onClick={() => setIsOpen(!isOpen)}
      >
        <User className="w-4 h-4 text-primary" />
        <span>{t('navigation.myAccount') || 'My Account'}</span>
        <ChevronDown className="w-4 h-4 text-muted-foreground transition-transform" style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)' }} />
      </Button>

      {/* Dropdown Menu */}
      <div className={`absolute right-0 mt-2 w-48 bg-white border border-border/40 rounded-xl shadow-lg z-50 overflow-hidden transition-opacity duration-200 ${isOpen ? 'opacity-100 visible' : 'opacity-0 invisible'}`}>
        {/* Menu Items */}
        <div className="py-1">
          <Link 
            to="/profile-1"
            className="flex items-center gap-3 px-4 py-2.5 hover:bg-primary/5 text-sm text-text transition-colors border-b border-border/40"
            onClick={() => setIsOpen(false)}
          >
            <User className="w-4 h-4 text-primary" />
            <span>{t('navigation.profile') || 'Profile'}</span>
          </Link>

          <Link 
            to="/logout"
            className="flex items-center gap-3 px-4 py-2.5 hover:bg-red-50 text-sm text-red-600 transition-colors border-t border-border/40"
            onClick={() => setIsOpen(false)}
          >
            <LogOut className="w-4 h-4" />
            <span>{t('navigation.logout')}</span>
          </Link>
        </div>
      </div>
    </div>
  );
}

export function Navbar() {
  const { t } = useTranslation();
  const location = useLocation();
  const { isLoggedIn } = useLoggedIn();

  const navLinks = React.useMemo(() => {
    return [
      {
        title: t('components.navbar.home'),
        href: "/",
      },
      {
        title: t('components.navbar.polyglot'),
        href: "/polyglot-legacy",
      },
      {
        title: t('components.navbar.pricing'),
        href: "/#pricing",
      },
      {
        title: t('navigation.africanlibrary'),
        href: "https://africanlanguagelibrary.tchamna.com/",
        external: true,
      },
      {
        title: t('components.navbar.contact'),
        href: "/contact",
      },
      {
        title: t('components.navbar.help'),
        href: "/help",
      },
    ];
  }, [t]);

  // Filter nav links based on current page and login status
  const filteredNavLinks = React.useMemo(() => {
    return navLinks.filter((link) => {
      if (link.showOnlyOnHome && location.pathname !== "/") {
        return false;
      }
      if (link.showOnlyWhenNotLoggedIn && isLoggedIn) {
        return false;
      }
      return true;
    });
  }, [navLinks, location.pathname, isLoggedIn]);

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 dark:bg-slate-950/95 backdrop-blur-xl border-b border-border/40 dark:border-slate-800/40 shadow-sm w-full will-change-transform">
      <div className="w-full px-3 sm:px-4 md:px-6 lg:px-8 flex items-center justify-between py-3 sm:py-4 min-h-[64px] sm:min-h-[72px] lg:min-h-[80px]">
        {/* Left Section - Logo */}
        <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0 min-w-0">
          <Logo variant="navbar" className="h-12 sm:h-14 md:h-16 flex-shrink-0" />
        </div>

        {/* Center Section - Navigation (Desktop Only) */}
        <div className="hidden lg:flex items-center gap-2 flex-grow min-w-0 px-4">
          <NavigationMenu className="min-w-0">
            <NavigationMenuList className="flex gap-1 flex-wrap justify-center">
              {filteredNavLinks.map((link, index) => (
                <NavigationMenuItem key={`${link.title}-${index}`} className="flex-shrink-0">
                  {link.external ? (
                    <a
                      href={link.href}
                      target="_blank"
                      rel="noreferrer"
                      className={`${navigationMenuTriggerStyle()} rounded-xl text-sm whitespace-nowrap`}
                    >
                      {link.title}
                    </a>
                  ) : (
                    <Link
                      to={link.href}
                      className={`${navigationMenuTriggerStyle()} rounded-xl text-sm whitespace-nowrap ${
                        location.pathname === link.href
                          ? "!bg-primary text-white hover:bg-primary hover:text-white dark:text-white dark:hover:text-white"
                          : ""
                      }`}
                    >
                      {link.title}
                    </Link>
                  )}
                </NavigationMenuItem>
              ))}
            </NavigationMenuList>
          </NavigationMenu>
        </div>

        {/* Right Section - Controls and Auth */}
        <div className="flex items-center gap-1 sm:gap-2 lg:gap-3 flex-shrink-0 ml-auto">
          {/* Language Selector and Theme Toggle */}
          <div className="hidden md:flex items-center gap-1 sm:gap-2 flex-shrink-0">
            <LanguageSelector variant="dropdown" />
            <ThemeToggle />
          </div>

          {/* Auth Buttons / Account Menu */}
          <div className="hidden lg:flex items-center gap-2 flex-shrink-0">
            {isLoggedIn ? (
              <AccountDropdown />
            ) : (
              <>
                <Link to="/get-started?tab=login" className="flex-shrink-0">
                  <Button variant="ghost" size="sm">{t('auth.login')}</Button>
                </Link>
                <Link to="/get-started?tab=signup" className="flex-shrink-0">
                  <Button variant="default" size="sm">{t('components.navbar.getStarted')}</Button>
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu */}
          <div className="lg:hidden">
            <MobileNavBar />
          </div>
        </div>
      </div>
    </header>
  );
}

export function MobileNavBar() {
  const { t } = useTranslation();
  const { user, isLoggedIn } = useLoggedIn();
  const parsedUser = React.useMemo(() => {
    if (!user) return null;
    try {
      return JSON.parse(user);
    } catch (error) {
      return null;
    }
  }, [user]);
  const [subscriptionExpiresAt, setSubscriptionExpiresAt] = React.useState(null);
  const [profileLoading, setProfileLoading] = React.useState(false);

  React.useEffect(() => {
    let isActive = true;
    if (!isLoggedIn) return undefined;
    const token = Cookie.get(JWT_COOKIE_NAME);
    if (!token) return undefined;

    setProfileLoading(true);
    fetch(API_BASE_URL + "/api/user/profile", {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        if (!isActive) return;
        setSubscriptionExpiresAt(
          data?.subscriptionExpiresAt || data?.subscription_expires_at || null
        );
      })
      .catch(() => {})
      .finally(() => {
        if (isActive) {
          setProfileLoading(false);
        }
      });

    return () => {
      isActive = false;
    };
  }, [isLoggedIn]);

  const navLinks = React.useMemo(() => {
    return [
      {
        title: t('components.navbar.home'),
        href: "/",
      },
      {
        title: t('components.navbar.polyglot'),
        href: "/polyglot-legacy",
      },
      {
        title: t('components.navbar.pricing'),
        href: "/#pricing",
      },
      {
        title: t('navigation.africanlibrary'),
        href: "https://africanlanguagelibrary.tchamna.com/",
        external: true,
      },
      {
        title: t('components.navbar.contact'),
        href: "/contact",
      },
      {
        title: t('components.navbar.help'),
        href: "/help",
      },
    ];
  }, [t]);

  const location = useLocation();

  // Filter nav links based on current page and login status
  const filteredNavLinks = React.useMemo(() => {
    return navLinks.filter((link) => {
      if (link.showOnlyOnHome && location.pathname !== "/") {
        return false;
      }
      if (link.showOnlyWhenNotLoggedIn && isLoggedIn) {
        return false;
      }
      return true;
    });
  }, [navLinks, location.pathname, isLoggedIn]);

  const displayName = parsedUser?.name || parsedUser?.email || "Signed in";
  const expiresLabel = subscriptionExpiresAt
    ? new Date(subscriptionExpiresAt).toLocaleDateString()
    : "Unknown";

  return (
    <Drawer>
      <DrawerTrigger asChild className="block lg:hidden">
        <Button variant="ghost">
          <Menu />
        </Button>
      </DrawerTrigger>
      <DrawerContent className="flex flex-col max-h-[100vh] overflow-hidden">
        <div className="mx-auto w-full max-w-sm flex flex-col h-full overflow-hidden">
          <DrawerHeader className="flex-shrink-0">
            <DrawerTitle>
              <img
                src={API_BASE_URL + "/frontend_assets/logo.png"}
                alt="the african polyglot logo"
                className="h-20 sm:h-24"
              />
            </DrawerTitle>
          </DrawerHeader>
          <div className="p-4 pb-0 flex-1 overflow-y-auto">
            <div className="flex items-start flex-col justify-start">
              {isLoggedIn && (
                <p className="mb-4 px-3 text-xs text-muted-foreground">
                  {displayName}, expires: {profileLoading ? "Loading..." : expiresLabel}
                </p>
              )}
              {filteredNavLinks.map((item, index) =>
                item.external ? (
                  <a
                    className="flex flex-1 items-center justify-between py-4 text-sm font-medium transition-all hover:underline text-left"
                    href={item.href}
                    target="_blank"
                    rel="noreferrer"
                    key={`${item.href}-${index}`}
                  >
                    {item.title}
                  </a>
                ) : (
                  <Link
                    className="flex flex-1 items-center justify-between py-4 text-sm font-medium transition-all hover:underline text-left [&[data-state=open]>svg]:rotate-180"
                    to={item.href}
                    key={`${item.href}-${index}`}
                  >
                    {item.title}
                  </Link>
                )
              )}
            </div>
          </div>
          <DrawerFooter className="flex-shrink-0">
            <div className="mb-4 w-full">
              <p className="text-sm font-medium mb-3">{t('common.language')}</p>
              <div className="flex items-center gap-2">
                <div className="flex-1">
                  <LanguageSelector variant="dropdown" />
                </div>
                <ThemeToggle />
              </div>
            </div>
            <div className="border-t pt-4 mb-4 w-full flex flex-col gap-2">
              {isLoggedIn ? (
                <Link to="/logout" className="w-full">
                  <Button variant="outline" className="w-full">{t('navigation.logout')}</Button>
                </Link>
              ) : (
                <>
                  <Link to="/get-started?tab=login" className="w-full">
                    <Button variant="ghost" className="w-full">{t('auth.login')}</Button>
                  </Link>
                  <Link to="/get-started?tab=signup" className="w-full">
                    <Button variant="default" className="w-full">{t('components.navbar.getStarted')}</Button>
                  </Link>
                </>
              )}
            </div>
            <DrawerClose asChild>
              <Button variant="outline">{t('buttons.close')}</Button>
            </DrawerClose>
          </DrawerFooter>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
